import { Component, ViewEncapsulation, OnDestroy } from '@angular/core';
import { BehaviorSubject, timer, Subject, interval } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import supportedBrowsers from './supportedBrowsers';
import { BROWSER_NOT_SUPPORTED_BANNER_SHOWN_KEY } from 'src/providers/session.provider';
import { safeSessionStorageGetItem, safeSessionStorageSetItem } from 'src/app/utils/storage';

@Component({
  selector: 'app-browser-not-supported',
  templateUrl: './browser-not-supported.component.html',
  styleUrls: ['./browser-not-supported.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  animations: [
    trigger('fadeInOut', [
      state('in', style({ opacity: 1, transform: 'translateY(0)' })),
      state('out', style({ opacity: 0, transform: 'translateY(-100%)' })),
      transition('in => out', animate('300ms ease-in')),
      transition('out => in', animate('300ms ease-out')),
    ]),
  ],
})
export class BrowserNotSupportedComponent implements OnDestroy {

  public isSupported$ = new BehaviorSubject<boolean>(true);

  public showBanner$ = new BehaviorSubject<boolean>(false);

  public animationState = 'out';

  public progressPercentage$ = new BehaviorSubject<number>(0);

  private readonly BANNER_DISPLAY_DURATION = 15000; // 15 seconds

  private readonly PROGRESS_UPDATE_INTERVAL = 100; // Update progress every 100ms

  private readonly FADE_OUT_DELAY = 200; // Small delay before starting fade out

  private destroy$ = new Subject<void>();

  constructor() {
    const isSupported = supportedBrowsers.test(navigator.userAgent);
    this.isSupported$.next(isSupported);
    
    if (!isSupported) {
      this.handleUnsupportedBrowser();
    }
  }

  private handleUnsupportedBrowser(): void {
    // Check if banner was already shown this session
    const bannerShownThisSession = safeSessionStorageGetItem(BROWSER_NOT_SUPPORTED_BANNER_SHOWN_KEY) === 'true';
    
    if (!bannerShownThisSession) {
      // Show the banner
      this.showBanner$.next(true);
      this.animationState = 'in';
      this.progressPercentage$.next(0);
      
      // Mark as shown in session storage
      safeSessionStorageSetItem(BROWSER_NOT_SUPPORTED_BANNER_SHOWN_KEY, 'true');
      
      // Start progress bar animation
      this.startProgressBar();
      
      // Start fade out animation after the specified duration
      timer(this.BANNER_DISPLAY_DURATION)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.animationState = 'out';
          
          // Hide the banner completely after animation finishes
          timer(this.FADE_OUT_DELAY + 300) // 300ms animation + delay
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.showBanner$.next(false);
              this.progressPercentage$.next(0);
            });
        });
    }
  }

  private startProgressBar(): void {
    const totalSteps = this.BANNER_DISPLAY_DURATION / this.PROGRESS_UPDATE_INTERVAL;
    let currentStep = 0;

    interval(this.PROGRESS_UPDATE_INTERVAL)
      .pipe(
        takeUntil(this.destroy$),
        map(() => {
          currentStep++;
          return Math.min((currentStep / totalSteps) * 100, 100);
        }),
      )
      .subscribe(percentage => {
        this.progressPercentage$.next(percentage);
        
        // Stop when we reach 100% or the display duration
        if (percentage >= 100) {
          return;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
